import { useSelector, useDispatch } from 'react-redux';
import React, { useMemo, useCallback } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { NotificationService, NOTIFICATION_TYPE_SUCCESS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import Global from '../../store/global-selectors';
import { emptyObject } from 'ui-components/default-props/empty';
import { GridHeaderWithOverflow } from '../../components/grid-header/grid-header-with-overflow';
import { ShippingPageOverflowDropdownWrapper } from './grid-header-overflow-dropdown-container';
import EditColumnsButtonContainer from '../../components/edit-columns-button/containers/shipping-grid-container';
import { shippingGridViews } from '../../components/shipping-grid/views';
import {
  shipmentGridViewIdConstants,
  SHIPMENT_PICKUPS_VIEW_ID,
  RECENT_VIEW_ID,
} from '../../components/shipping-grid/views/view-id-constants';
import { useShipmentsGridAvailableButtonsForView } from './use-shipments-grid-available-buttons-for-view';
import { usePusherEvent } from '../../hooks/use-pusher-event';
import { silentRequestGetShipments } from '../../store/shipments/shipments.actions';
import { CANCEL_SHIPMENT_REFUNDS_JOB_COMPLETE } from '../../services/pusher-service/pusher-contract-messages';
import messages from '../../components/grid-header/messages';

const V2_CARRIER_PICKUP_VIEW = '/v2carrierpickup';

const deriveViewId = (match) => {
  const {
    params: { statusViewId },
    path,
  } = match;

  if (path === V2_CARRIER_PICKUP_VIEW) {
    return SHIPMENT_PICKUPS_VIEW_ID;
  }
  // If there isn't a statusViewId, we are on EOD, Pickup or Batches. We need to derive the id from the path;
  const derived = statusViewId || path.split('/')[2];
  return shipmentGridViewIdConstants.includes(derived) ? derived : RECENT_VIEW_ID; // default to recent here just incase.
};

const useGetButtonsFromView = (view) => {
  const buttons = useShipmentsGridAvailableButtonsForView(view.id);

  return useMemo(() => {
    return buttons.reduce(
      (acc, button, i) => {
        const { id, overflowName, menu: MenuComponent, type: ButtonComponent, requiredRender } = button;

        if (requiredRender) {
          acc.requiredRenderIndices.push(i);
        }

        acc.buttonComponents.push(
          <ButtonComponent key={i} id={id} overflowName={overflowName}>
            {MenuComponent && <MenuComponent />}
          </ButtonComponent>,
        );

        return acc;
      },
      { buttonComponents: [], requiredRenderIndices: [] },
    );
  }, [buttons]);
};

const useGetViewFromMatch = (match) => {
  const derivedViewId = deriveViewId(match);
  const view = shippingGridViews.find((v) => v.id === derivedViewId) || emptyObject;
  const { buttonComponents, requiredRenderIndices } = useGetButtonsFromView(view);

  return useMemo(() => {
    return {
      ...view,
      label: view.expandedDescription || view.label,
      buttons: buttonComponents,
      requiredRenderIndices,
    };
  }, [buttonComponents, requiredRenderIndices, view]);
};

const ShippingGridHeader = () => {
  const dispatch = useDispatch();
  const searchTerm = useSelector(Global.selectShipmentsGridSearchTerm);
  const match = useRouteMatch();
  const view = useGetViewFromMatch(match);
  const isEditColumnButtonVisible = view.id !== SHIPMENT_PICKUPS_VIEW_ID;

  // [Tech Debt]: https://auctane.atlassian.net/browse/SPD-22636
  const handleBulkActionComplete = useCallback(
    (pusherMessage) => {
      if (pusherMessage.data.succeeded) {
        NotificationService.showNotification({
          title: '',
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          content: <FormattedMessage {...messages.excludeFromRefundSuccess} />,
          duration: 5000,
        });
        dispatch(silentRequestGetShipments());
      } else {
        NotificationService.showNotification({
          title: '',
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.excludeFromRefundError} />,
          duration: 3000,
        });
      }
    },
    [dispatch],
  );

  usePusherEvent(CANCEL_SHIPMENT_REFUNDS_JOB_COMPLETE, handleBulkActionComplete);

  return (
    <GridHeaderWithOverflow customOverflowDropdown={ShippingPageOverflowDropdownWrapper} searchTerm={searchTerm} view={view}>
      {isEditColumnButtonVisible && <EditColumnsButtonContainer />}
    </GridHeaderWithOverflow>
  );
};

export default ShippingGridHeader;
